import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  ArrowLeft, 
  Save, 
  X, 
  FileText,
  Eye,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ProposalTemplate, TemplateType } from '@/types/business-opportunities';
import { 
  getProposalTemplate, 
  createProposalTemplate, 
  updateProposalTemplate 
} from '@/services/business-opportunities';
import { sanitizeTemplateHTML } from '@/utils/htmlSanitizer';

// Sample template for new proposals
const SAMPLE_HTML_TEMPLATE = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Proposta Comercial</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 0;
      color: #333;
    }
    .container {
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
    }
    .header {
      text-align: center;
      margin-bottom: 40px;
    }
    .logo {
      max-width: 200px;
      margin-bottom: 20px;
    }
    h1 {
      color: #2563eb;
      margin-bottom: 10px;
    }
    .section {
      margin-bottom: 30px;
    }
    .section-title {
      border-bottom: 2px solid #2563eb;
      padding-bottom: 5px;
      margin-bottom: 15px;
      color: #2563eb;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    th, td {
      border: 1px solid #ddd;
      padding: 10px;
      text-align: left;
    }
    th {
      background-color: #f5f5f5;
    }
    .footer {
      margin-top: 50px;
      text-align: center;
      font-size: 14px;
      color: #666;
    }
    .signature {
      margin-top: 50px;
      border-top: 1px solid #ddd;
      padding-top: 20px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <img src="{{logo_url}}" alt="Logo da Empresa" class="logo">
      <h1>Proposta Comercial</h1>
      <p>Referência: #{{proposal_id}}</p>
    </div>

    <div class="section">
      <h2 class="section-title">Informações do Cliente</h2>
      <p><strong>Cliente:</strong> {{client_name}}</p>
      <p><strong>CNPJ/CPF:</strong> {{client_document}}</p>
      <p><strong>Endereço:</strong> {{client_address}}</p>
      <p><strong>Contato:</strong> {{client_contact}}</p>
      <p><strong>Email:</strong> {{client_email}}</p>
      <p><strong>Telefone:</strong> {{client_phone}}</p>
    </div>

    <div class="section">
      <h2 class="section-title">Descrição da Proposta</h2>
      <p>{{proposal_description}}</p>
    </div>

    <div class="section">
      <h2 class="section-title">Itens da Proposta</h2>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Descrição</th>
            <th>Quantidade</th>
            <th>Valor Unitário</th>
            <th>Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {{#each items}}
          <tr>
            <td>{{index}}</td>
            <td>{{description}}</td>
            <td>{{quantity}}</td>
            <td>{{currency}} {{unit_price}}</td>
            <td>{{currency}} {{total_price}}</td>
          </tr>
          {{/each}}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4" style="text-align: right;"><strong>Total:</strong></td>
            <td>{{currency}} {{total_value}}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <div class="section">
      <h2 class="section-title">Condições Comerciais</h2>
      <p><strong>Validade da Proposta:</strong> {{validity_days}} dias</p>
      <p><strong>Forma de Pagamento:</strong> {{payment_terms}}</p>
      <p><strong>Prazo de Entrega:</strong> {{delivery_time}}</p>
    </div>

    <div class="section">
      <h2 class="section-title">Observações</h2>
      <p>{{observations}}</p>
    </div>

    <div class="signature">
      <p>Atenciosamente,</p>
      <p>{{representative_name}}</p>
      <p>{{company_name}}</p>
      <p>{{company_phone}}</p>
      <p>{{company_email}}</p>
    </div>

    <div class="footer">
      <p>{{company_name}} - {{company_document}}</p>
      <p>{{company_address}}</p>
      <p>Proposta gerada em {{generated_date}}</p>
    </div>
  </div>
</body>
</html>
`;

const SAMPLE_CSS_TEMPLATE = `
body {
  font-family: Arial, sans-serif;
  margin: 0;
  padding: 0;
  color: #333;
}
.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}
.header {
  text-align: center;
  margin-bottom: 40px;
}
.logo {
  max-width: 200px;
  margin-bottom: 20px;
}
h1 {
  color: #2563eb;
  margin-bottom: 10px;
}
.section {
  margin-bottom: 30px;
}
.section-title {
  border-bottom: 2px solid #2563eb;
  padding-bottom: 5px;
  margin-bottom: 15px;
  color: #2563eb;
}
table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}
th, td {
  border: 1px solid #ddd;
  padding: 10px;
  text-align: left;
}
th {
  background-color: #f5f5f5;
}
.footer {
  margin-top: 50px;
  text-align: center;
  font-size: 14px;
  color: #666;
}
.signature {
  margin-top: 50px;
  border-top: 1px solid #ddd;
  padding-top: 20px;
}
`;

const ProposalTemplateForm: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const isEditing = id !== 'new' && id !== undefined;
  
  // Estado do formulário
  const [template, setTemplate] = useState<Partial<ProposalTemplate>>({
    name: '',
    description: '',
    template_type: 'services' as TemplateType,
    html_content: '',
    css_content: '',
    is_active: true
  });
  
  // Estados para UI
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Tipos de templates disponíveis
  const templateTypes = [
    { value: 'services' as TemplateType, label: 'Serviço' },
    { value: 'products' as TemplateType, label: 'Produto' },
    { value: 'licensing' as TemplateType, label: 'Licenciamento' },
    { value: 'consultancy' as TemplateType, label: 'Consultoria' },
    { value: 'subscription' as TemplateType, label: 'Assinatura' }
  ];
  
  // Carrega o template se estiver editando ou inicia com o modelo exemplo se for novo
  useEffect(() => {
    if (isEditing && id) {
      fetchTemplate(id);
    } else {
      // Se for um novo template, inicializa com o modelo de exemplo
      setTemplate(prev => ({
        ...prev,
        html_content: SAMPLE_HTML_TEMPLATE,
        css_content: SAMPLE_CSS_TEMPLATE
      }));
    }
  }, [id, isEditing]);
  
  const fetchTemplate = async (templateId: string) => {
    try {
      setIsLoading(true);
      const data = await getProposalTemplate(templateId);
      
      if (data) {
        // Map template_html from DB to html_content in our UI
        setTemplate({
          id: data.id,
          name: data.name,
          description: data.description || '',
          template_type: data.template_type as TemplateType,
          html_content: data.template_html || '',
          css_content: data.css_content || '',
          is_active: data.is_active,
          organization_id: data.organization_id
        });
      }
    } catch (error) {
      console.error('Erro ao buscar template:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados do template.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTemplate(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setTemplate(prev => ({ ...prev, [name]: value }));
  };
  
  const handleCheckboxChange = (name: string, checked: boolean) => {
    setTemplate(prev => ({ ...prev, [name]: checked }));
  };
  
  const handlePreview = () => {
    setShowPreview(true);
  };
  
  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      if (!template.name || !template.template_type || !template.html_content) {
        toast({
          title: 'Campos obrigatórios',
          description: 'Preencha todos os campos obrigatórios.',
          variant: 'destructive',
        });
        return;
      }
      
      if (isEditing && id) {
        await updateProposalTemplate(id, template);
        toast({
          title: 'Template atualizado',
          description: 'O template de proposta foi atualizado com sucesso.',
        });
      } else {
        await createProposalTemplate(template);
        toast({
          title: 'Template criado',
          description: 'O template de proposta foi criado com sucesso.',
        });
      }
      
      navigate('/proposal-templates');
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar o template.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Adicionar um botão para carregar o template de exemplo
  const loadSampleTemplate = () => {
    setTemplate(prev => ({
      ...prev,
      html_content: SAMPLE_HTML_TEMPLATE,
      css_content: SAMPLE_CSS_TEMPLATE
    }));
    
    toast({
      title: "Template de exemplo carregado",
      description: "Um modelo básico de proposta foi carregado para você personalizar.",
    });
  };
  
  return (
    <div className="container mx-auto p-4">
      <div className="mb-4">
        <Button 
          variant="outline" 
          onClick={() => navigate('/proposal-templates')}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para Templates
        </Button>
      </div>
      
      <Card className="w-full mb-6">
        <CardHeader className="bg-card">
          <CardTitle className="text-2xl font-extrabold">
            {isEditing ? 'Editar Template de Proposta' : 'Novo Template de Proposta'}
          </CardTitle>
          <CardDescription>
            {isEditing 
              ? 'Edite os detalhes e o conteúdo HTML do template'
              : 'Crie um novo template para suas propostas comerciais'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Informações Básicas */}
              <div className="space-y-4">
                <h3 className="text-lg font-extrabold">Informações Básicas</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="required">Nome</Label>
                    <Input
                      id="name"
                      name="name"
                      value={template.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="template_type" className="required">Tipo de Template</Label>
                    <Select
                      value={template.template_type}
                      onValueChange={(value) => handleSelectChange('template_type', value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {templateTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="description">Descrição</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={template.description}
                      onChange={handleInputChange}
                      placeholder="Descreva brevemente o propósito deste template"
                      rows={3}
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_active"
                    checked={template.is_active}
                    onCheckedChange={(checked) => 
                      handleCheckboxChange('is_active', checked as boolean)
                    }
                  />
                  <Label htmlFor="is_active">Template ativo</Label>
                </div>
              </div>
              
              {/* Conteúdo HTML */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-extrabold required">Conteúdo HTML</h3>
                  {!isEditing && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={loadSampleTemplate}
                      className="text-xs"
                    >
                      Carregar Template de Exemplo
                    </Button>
                  )}
                </div>
                <Alert className="mb-4 bg-blue-50 text-blue-800 border-blue-200">
                  <FileText className="h-4 w-4" />
                  <AlertTitle>Importante</AlertTitle>
                  <AlertDescription>
                    O código HTML aqui inserido será usado para gerar a proposta. 
                    Você pode usar variáveis como &#123;&#123;client_name&#125;&#125;, &#123;&#123;total_value&#125;&#125; e outras
                    que serão substituídas pelos dados reais ao gerar a proposta.
                  </AlertDescription>
                </Alert>
                <Textarea
                  id="html_content"
                  name="html_content"
                  value={template.html_content}
                  onChange={handleInputChange}
                  placeholder="<div>Seu código HTML aqui...</div>"
                  className="font-mono text-sm"
                  rows={15}
                  required
                />
              </div>
              
              {/* CSS Customizado */}
              <div className="space-y-4">
                <h3 className="text-lg font-extrabold">CSS Customizado (opcional)</h3>
                <Textarea
                  id="css_content"
                  name="css_content"
                  value={template.css_content}
                  onChange={handleInputChange}
                  placeholder="body { font-family: Arial; }"
                  className="font-mono text-sm"
                  rows={8}
                />
              </div>
            </div>
          )}
        </CardContent>
        
        <CardFooter className="flex justify-between border-t p-6">
          <Button 
            variant="outline" 
            onClick={() => navigate('/proposal-templates')}
            className="gap-2"
          >
            <X className="h-4 w-4" /> Cancelar
          </Button>
          
          <div className="flex gap-2">
            <Button 
              variant="secondary" 
              onClick={handlePreview}
              className="gap-2"
              disabled={!template.html_content}
            >
              <Eye className="h-4 w-4" /> Pré-visualizar
            </Button>
            
            <Button 
              onClick={handleSave}
              disabled={isSaving || !template.name || !template.template_type || !template.html_content}
              className="gap-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-b-transparent"></div>
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" /> Salvar Template
                </>
              )}
            </Button>
          </div>
        </CardFooter>
      </Card>
      
      {/* Modal de Previsualização */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-screen">
          <DialogHeader>
            <DialogTitle>Prévia do Template</DialogTitle>
            <DialogDescription>
              {template.name} - {templateTypes.find(t => t.value === template.template_type)?.label}
            </DialogDescription>
          </DialogHeader>
          
          <div className="border rounded p-4 overflow-auto max-h-[70vh]">
            <style>{template.css_content}</style>
            <div dangerouslySetInnerHTML={{ __html: sanitizeTemplateHTML(template.html_content || '') }} />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProposalTemplateForm; 